import React, { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PersonBadge,
  BoxSeam,
  BarChartLine,
  People,
  Trash3,
  ExclamationTriangle,
  Lightning,
  PersonPlus,
  Search,
  XLg,
  Wallet2,
  Receipt,
  Check2,
  Megaphone,
  Calendar3,
  PlusLg,
  ChevronRight,
  BoxArrowUpRight,
  Truck,
  Gift,
  Tag,
  Phone,
  CalendarEvent,
  Trophy,
  PersonLinesFill,
  PersonHeart,
  CreditCard,
  Sliders,
  Rocket,
  ArrowRepeat,
  CheckCircleFill,
  ExclamationCircleFill,
} from 'react-bootstrap-icons';
import DiscountsTab from './DiscountsTab';
import { useLocale } from '../i18n/LocaleContext';
import { useAuth } from '../auth/AuthContext';
import { tabAvailable } from '../config/embedded';
import {
  updateBrandProfile,
  updateBrandIban,
  toggleBrandVisibility,
  deleteBrand,
  fetchBrandProducts,
  syncProducts,
  fetchSyncProgress,
  fetchBrand,
  fetchBrandMembers,
  addBrandMember,
  removeBrandMember,
  updateBrandMember,
  searchUsers,
  fetchStatsOverview,
  fetchRevenue,
  fetchBrandOrders,
  fetchPayouts,
  requestPayout,
  permissionsForUser,
  BRAND_PERMISSIONS,
  fetchBrandCampaigns,
  fetchBrandCampaign,
  createBrandCampaign,
  selectCampaignApplicant,
  rejectCampaignApplicant,
  fetchLabelDisputes,
} from '../api/brands';
import ShippingTab from './ShippingTab';
import DropsTab from './drops/DropsTab';
import { brandHasShopify } from '../api/onboarding';
import { startProductCrawl, fetchCrawlStatus, CrawlUnavailable } from '../api/embed';
import { useFormat } from './embed/EmbedShared';
import GamesTab from './GamesTab';
import LabelingBadge from './LabelingBadge';
import LabelDispute from './LabelDispute';
import useLabelingPoll from './useLabelingPoll';
import { hasPendingLabeling, withLabeling, latestDisputesByProduct, LABELING_POLL_MS } from './productLabeling';
import { LogoField, CoverField, useImagePick } from './brand/BrandMedia';
import { profileSupport, profileErrorKey, normalizeIbanInput } from './brand/profileSave';
import { SHOW_BILLING_PAGE, SHOW_FOLLOWERS_PAGE } from '../config/embedFlags';
import './BrandDashboard.scss';

// The fit-check embed pages load on demand: the chart library they bring is
// only needed there.
const OnboardingTab = lazy(() => import('./embed/OnboardingTab'));
const EmbedStatsTab = lazy(() => import('./embed/EmbedStatsTab'));
const EmbedVisitorsTab = lazy(() => import('./embed/EmbedVisitorsTab'));
const EmbedSettingsTab = lazy(() => import('./embed/EmbedSettingsTab'));
const FollowersTab = lazy(() => import('./brand/FollowersTab'));
// Stripe Elements come in with this page only.
const BillingTab = lazy(() => import('./billing/BillingTab'));

// What the signed-in user may do on this brand.
//
// Embedded: the session came from a Shopify session token, which Shopify only
// issues to staff of this shop. There is no Outfino member row to derive
// permissions from (stores[].members is empty), so the merchant is treated as
// the owner. Which tabs actually exist there is a separate question — see
// tabAvailable / config/embedded.js.
function useBrandPermissions(brand) {
  const { user, embedded } = useAuth();
  return useMemo(() => {
    if (embedded) return new Set(BRAND_PERMISSIONS);
    if (!brand) return new Set();
    return permissionsForUser(brand, user?.id, user?.admin);
  }, [brand, user, embedded]);
}

// Every brand page with the permission it needs. `group` is the sidebar
// category it sits in, `parent` the page it is a sub-page of; BRAND_NAV is the
// sidebar order, where a `tab` entry is a page shown at the top level.
export const BRAND_NAV = [
  // Beüzemelés: the guided "get live" page — always first, above everything.
  { tab: 'onboarding', visible: true },
  { group: 'mobile', Icon: Phone, visible: false },
  { tab: 'campaigns', visible: false },
  // Flat top-level pages (no group): the fit-check embed pages, followers,
  // drops, settings (with the brand profile on top), billing, members, the danger
  // zone.
  { tab: 'embedded-stats', visible: true },
  { tab: 'embedded-visitors', visible: true },
  { tab: 'followers', visible: true },
  // Dropok: manual products only (CEO) — components/drops/DropsTab.js.
  { tab: 'drops', visible: true },
  { tab: 'embedded-settings', visible: true },
  { tab: 'billing', visible: true },
  { tab: 'members', visible: true },
  { tab: 'special', visible: true },
];

/// The page the bare dashboard path opens: Statisztikák when this user may
/// open it, otherwise the first page in the menu.
export const DEFAULT_TAB = 'embedded-stats';
export const LANDING_TAB = DEFAULT_TAB;

export function landingTab(tabs) {
  return tabs.some((x) => x.key === DEFAULT_TAB) ? DEFAULT_TAB : tabs[0]?.key ?? null;
}

const HIDDEN_NAV = new Set(BRAND_NAV.filter((entry) => !entry.visible).map((entry) => entry.group || entry.tab));

// Pages behind a release flag (config/embedFlags.js).
export function releasedTab(key, flags = { followers: SHOW_FOLLOWERS_PAGE, billing: SHOW_BILLING_PAGE }) {
  return flags[key] !== false;
}

const navVisible = ({ key, group }) => !HIDDEN_NAV.has(group || key) && releasedTab(key);

// The Tagok toggles: "Számlázás kezelése" only while Számlázás is shown.
const MEMBER_PERMISSIONS = BRAND_PERMISSIONS.filter((p) => p !== 'manageBilling' || SHOW_BILLING_PAGE);

const BRAND_TABS = [
  // Beüzemelés: the first-run guided setup (billing, the button, products, test).
  // Owner / editBrand (it hosts the embed settings + the key).
  { key: 'onboarding', Icon: Rocket, perm: 'editBrand' },
  // The app's full brand profile (company, tagline, IBAN, visibility…), with
  // the hidden mobile pages. Its name, logo and cover part is the Márkaprofil
  // section of Beállítások.
  { key: 'profile', group: 'mobile', Icon: PersonBadge, perm: null },
  { key: 'products', group: 'mobile', Icon: BoxSeam, perm: 'viewProducts' },
  { key: 'discounts', group: 'mobile', Icon: Tag, perm: 'viewProducts', shopify: true },
  // Challenges / Játékok — galaxy gift drops + the prize wheel (GamesTab). A
  // marketing tool, so it needs the campaign permission.
  { key: 'challenges', group: 'mobile', Icon: Gift, perm: 'manageCampaigns' },
  { key: 'stats', group: 'mobile', Icon: BarChartLine, perm: 'viewAnalytics' },
  { key: 'orders', group: 'mobile', Icon: Receipt, perm: 'viewAnalytics', shopify: true },
  { key: 'events', group: 'mobile', Icon: CalendarEvent, perm: 'viewProducts' },
  { key: 'prize-pool', group: 'mobile', Icon: Trophy, perm: 'manageCampaigns' },
  { key: 'shipping', group: 'mobile', Icon: Truck, perm: 'viewProducts', shopify: true },
  { key: 'payouts', group: 'mobile', Icon: Wallet2, perm: 'viewAnalytics' },
  { key: 'campaigns', Icon: Megaphone, perm: 'manageCampaigns' },
  { key: 'embedded-stats', Icon: BarChartLine, perm: 'viewAnalytics' },
  { key: 'embedded-visitors', Icon: PersonLinesFill, perm: 'viewAnalytics' },
  { key: 'followers', Icon: PersonHeart, perm: 'viewAnalytics' },
  // Drops of manually uploaded products (POST /brands/:id/products/manual →
  // POST /drops/batch); viewing needs viewProducts, changes editProducts.
  { key: 'drops', Icon: Lightning, perm: 'viewProducts' },
  // The key, the button's design and the code to paste: brand settings.
  { key: 'embedded-settings', Icon: Sliders, perm: 'editBrand' },
  // Everyone on the brand sees the page; the owner (or the billing
  // permission) changes the card and the details — see api/billing.js.
  { key: 'billing', Icon: CreditCard, perm: null },
  { key: 'members', Icon: People, perm: 'manageMembers' },
  { key: 'special', Icon: ExclamationTriangle, perm: 'editBrand' },
];

// Pages that were removed: an old link lands on the page that replaced them
// (`#anchor` = the section on that page).
export const TAB_REDIRECTS = {
  profile: 'embedded-settings#brand-profile',
  'embedded-categories': 'embedded-stats',
  'embedded-buyers': 'embedded-visitors',
  'embedded-viewers': 'embedded-visitors',
};

const COMING_SOON_TABS = new Set([
  'events', 'prize-pool',
]);

// The pages this user can open on this brand. Gated by permission, mirroring
// the mobile app.
//
// `shopify: true` pages (orders, shipping, discounts) read the brand's Shopify
// store, so a brand without one (made in the portal) does not get them.
export function useBrandTabs(brand) {
  const perms = useBrandPermissions(brand);
  const shopify = brandHasShopify(brand);
  return useMemo(
    () => BRAND_TABS.filter((tab) => (!tab.perm || perms.has(tab.perm)) && (!tab.shopify || shopify) && tabAvailable(tab.key) && navVisible(tab)),
    [perms, shopify]
  );
}

// The body of the selected brand page. Which page is chosen in the dashboard
// sidebar (Dashboard.js); a tab this user can't open renders nothing.
export default function BrandDashboard({ brand, tab, connected = null, onConnectionChange, onOpenSettings, onNavigate }) {
  const { t } = useLocale();
  const { user, refresh, embedded } = useAuth();
  const perms = useBrandPermissions(brand);

  return (
    <div className="brand-dash">
      <div className="brand-dash__head">
        <div className="brand-dash__id">
          {brand.logo ? (
            <img className="brand-dash__logo" src={brand.logo} alt={brand.name} />
          ) : (
            <span className="brand-dash__logo brand-dash__logo--ph">
              {brand.name?.[0]?.toUpperCase() || 'B'}
            </span>
          )}
          <div>
            <h1 className="brand-dash__name">
              {brand.name}
              {user?.environment === 'dev' && brand.environment === 'dev' && (
                <span className="brand-dash__dev">{t('shell.envDev')}</span>
              )}
            </h1>
            {/* Prefer the brand description; fall back to the website/domain. */}
            {(brand.description || brand.website) && (
              <span className="brand-dash__site">
                {brand.description || brand.website}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="brand-dash__panel">
        {tab === 'onboarding' && perms.has('editBrand') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <OnboardingTab brand={brand} onNavigate={(key) => { onConnectionChange?.(); onNavigate?.(key); }} />
          </Suspense>
        )}
        {tab === 'profile' && (
          <ProfileTab brand={brand} canEdit={perms.has('editBrand')} onSaved={refresh} />
        )}
        {tab === 'followers' && tabAvailable(tab) && perms.has('viewAnalytics') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <FollowersTab brand={brand} />
          </Suspense>
        )}
        {tab === 'billing' && tabAvailable(tab) && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <BillingTab brand={brand} />
          </Suspense>
        )}
        {tab === 'products' && perms.has('viewProducts') && (
          <ProductsTab
            brand={brand}
            canSync={perms.has('editProducts') && brandHasShopify(brand)}
            // A website crawl is for brands that DON'T sync from Shopify: the
            // crawl only ever reads the brand's own saved site_domain. In the
            // Shopify admin iframe the endpoint isn't offered (feature-detected
            // to a "hamarosan" state anyway), so we don't offer it there.
            canCrawl={perms.has('editProducts') && !embedded && !brandHasShopify(brand)}
            canRelabel={perms.has('editProducts')}
            onNavigate={onNavigate}
          />
        )}
        {tab === 'drops' && tabAvailable('drops') && perms.has('viewProducts') && (
          <DropsTab brand={brand} canEdit={!embedded && perms.has('editProducts')} />
        )}
        {tab === 'discounts' && tabAvailable('discounts') && perms.has('viewProducts') && (
          <DiscountsTab brand={brand} canEdit={perms.has('editProducts')} />
        )}
        {tab === 'campaigns' && tabAvailable('campaigns') && perms.has('manageCampaigns') && (
          <CampaignsTab brand={brand} canEdit={perms.has('manageCampaigns')} />
        )}
        {tab === 'challenges' && tabAvailable('challenges') && perms.has('manageCampaigns') && (
          <GamesTab brand={brand} canEdit={perms.has('manageCampaigns')} />
        )}
        {tab === 'stats' && perms.has('viewAnalytics') && <StatsTab brand={brand} />}
        {tab === 'orders' && perms.has('viewAnalytics') && brandHasShopify(brand) && <OrdersTab brand={brand} />}
        {tab === 'shipping' && tabAvailable('shipping') && perms.has('viewProducts') && brandHasShopify(brand) && (
          <ShippingTab brand={brand} />
        )}
        {tab === 'payouts' && perms.has('viewAnalytics') && <PayoutsTab brand={brand} />}
        {tab === 'members' && perms.has('manageMembers') && (
          <MembersTab
            brand={brand}
            currentUserId={user?.id}
            isAdmin={user?.admin}
          />
        )}
        {/* Fit-check embed (embed.outfino.io): Statisztikák, Látogatók,
            Beállítások. */}
        {tab === 'embedded-stats' && tabAvailable(tab) && perms.has('viewAnalytics') && (
          connected === false ? (
            <NotConnectedState onSetup={onOpenSettings} />
          ) : (
            <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
              <EmbedStatsTab brand={brand} />
            </Suspense>
          )
        )}
        {tab === 'embedded-visitors' && tabAvailable(tab) && perms.has('viewAnalytics') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <EmbedVisitorsTab brand={brand} />
          </Suspense>
        )}
        {tab === 'embedded-settings' && tabAvailable(tab) && perms.has('editBrand') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <EmbedSettingsTab brand={brand} canEditBrand={perms.has('editBrand')} onBrandSaved={() => { refresh(); onConnectionChange?.(); }} />
          </Suspense>
        )}
        {COMING_SOON_TABS.has(tab) && tabAvailable(tab) && (
          <div className="brand-dash__soon">{t('shell.comingSoon')}</div>
        )}
        {tab === 'special' && tabAvailable('special') && perms.has('editBrand') && (
          <DangerZone brand={brand} onDeleted={refresh} />
        )}
      </div>
    </div>
  );
}

// ── Danger zone (remove brand) ──────────────────────────────────────────────

function DangerZone({ brand, onDeleted }) {
  const { t } = useLocale();
  const { embedded } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleDelete() {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('danger.confirm', { name: brand.name }))) return;
    setBusy(true);
    setError('');
    try {
      await deleteBrand(brand.id);
    } catch (err) {
      setError(err?.message ? `${t('danger.error')} (${err.message})` : t('danger.error'));
      setBusy(false);
      return;
    }
    if (!embedded) navigate('/dashboard', { replace: true });
    if (onDeleted) await onDeleted();
  }

  return (
    <div className="danger-zone">
      <div>
        <div className="danger-zone__title">{t('danger.title')}</div>
        <div className="danger-zone__body">{t('danger.deleteBody')}</div>
        {error && <div className="danger-zone__error">{error}</div>}
      </div>
      <button className="danger-zone__btn" onClick={handleDelete} disabled={busy}>
        <Trash3 size={15} style={{ marginRight: 6 }} />
        {busy ? t('danger.deleting') : t('danger.deleteBrand')}
      </button>
    </div>
  );
}

// ── Profile tab (name, company, tagline, description, phone, address, logo, cover, IBAN, visibility; the website lives in Beállítások) ──

/// Max length of the one-line claim under the brand name — mirrors the API's
/// brands.tagline VARCHAR(140) (the server also trims / caps it).
export const BRAND_TAGLINE_MAX = 140;
/// brands.company_name VARCHAR(255) / brands.address VARCHAR(500) (the API refuses longer).
export const PROFILE_COMPANY_MAX = 255;
export const PROFILE_ADDRESS_MAX = 500;

export function ProfileTab({ brand, canEdit, onSaved }) {
  const { t } = useLocale();
  const { embedded } = useAuth() || {};
  const [name, setName] = useState(brand.name || '');
  const [company, setCompany] = useState(brand.companyName || '');
  const [tagline, setTagline] = useState(brand.tagline || '');
  const [description, setDescription] = useState(brand.description || '');
  const [phone, setPhone] = useState(brand.phone || '');
  const [address, setAddress] = useState(brand.address || '');
  const [iban, setIban] = useState('');
  const [storedIban, setStoredIban] = useState('');
  const [isActive, setIsActive] = useState(brand.isActive !== false);
  const [savedActive, setSavedActive] = useState(brand.isActive !== false);
  // What this API stores (components/brand/profileSave.js): null = not read yet.
  const [support, setSupport] = useState(() => profileSupport(null));
  const logo = useImagePick(brand.logo);
  // Cover image: picked file is uploaded with the profile (multipart `cover`,
  // see updateBrandProfile) and stored as storage/brands/<id>/cover.jpg; the
  // session brand carries it as `coverImage` (GET /brands/:id: `cover_image`).
  const cover = useImagePick(brand.coverImage || brand.cover_image);
  const [state, setState] = useState('idle'); // idle | saving | saved | error
  const [errorKey, setErrorKey] = useState('profile.saveError');
  // Drives the toast's exit animation: true = playing the fade-out before unmount.
  const [toastLeaving, setToastLeaving] = useState(false);

  // Outfino suspended the brand: the visibility switch cannot lift it.
  const suspended = brand.adminSuspended === true || support.suspended;

  // Dismiss the save toast gracefully: play the exit animation, then unmount.
  function dismissToast() {
    setToastLeaving(true);
    setTimeout(() => {
      setState('idle');
      setToastLeaving(false);
    }, 320); // must match the save-toast-out animation duration
  }

  // The session brand doesn't carry iban/is_active — fetch the full brand once.
  // The same read is the source of truth for the stored cover / tagline /
  // company name / address (an older session payload may predate them), and
  // tells what this API can store (profileSupport).
  useEffect(() => {
    let alive = true;
    fetchBrand(brand.id)
      .then((full) => {
        if (!alive || !full) return;
        const can = profileSupport(full);
        setSupport(can);
        if (full.iban != null) {
          setIban(full.iban);
          setStoredIban(full.iban);
        }
        if (full.is_active != null) {
          setIsActive(!!full.is_active);
          setSavedActive(!!full.is_active);
        }
        if (full.tagline != null) setTagline(full.tagline);
        if (can.profileFields) {
          setCompany(full.company_name || '');
          setAddress(full.address || '');
        }
        if (full.cover_image) cover.setInitial(full.cover_image);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  async function handleSave(e) {
    e.preventDefault();
    if (!canEdit) return;
    setState('saving');
    try {
      // Profile fields — CAMELCASE keys (mapped server-side); the picked logo
      // and cover go along as multipart files — see updateBrandProfile.
      // companyName / address only where the API stores them; `website` never
      // (the Beállítások "A webshop címe" field owns the web address).
      const fields = {
        storeName: name,
        tagline: tagline.trim().slice(0, BRAND_TAGLINE_MAX),
        description,
        phone,
        ...(support.profileFields ? { companyName: company, address } : {}),
      };
      const saved = await updateBrandProfile(brand.id, fields, logo.file, cover.file);
      // Swap the local object-URL previews for the stored, cache-busted files
      // the API answered with (so what the merchant sees is what the app gets),
      // and show the stored text (the API trims / folds whitespace).
      if (saved && typeof saved === 'object') {
        logo.setStored(saved.logo);
        cover.setStored(saved.cover_image);
        if (saved.tagline != null) setTagline(saved.tagline || '');
        if (support.profileFields && 'company_name' in saved) setCompany(saved.company_name || '');
        if (support.profileFields && 'address' in saved) setAddress(saved.address || '');
      }
      // The payout IBAN (snake_case, same endpoint): only where the API stores
      // it and this user may change it, and only when it changed (clearing
      // included). The API checks it (mod-97) and answers it normalised.
      if (support.canManageBilling && normalizeIbanInput(iban) !== normalizeIbanInput(storedIban)) {
        const withIban = await updateBrandIban(brand.id, iban);
        const stored = withIban && typeof withIban === 'object' ? withIban.iban ?? '' : normalizeIbanInput(iban);
        setIban(stored || '');
        setStoredIban(stored || '');
      }
      // The brand's own visibility — never while Outfino suspended it.
      if (!suspended && savedActive !== isActive) {
        await toggleBrandVisibility(brand.id, isActive);
        setSavedActive(isActive);
      }
      setState('saved');
      // Reflect the saved values locally so the form doesn't appear to "do
      // nothing": the parent re-renders with the same brand id (stable key), so
      // ProfileTab won't remount — we already hold the latest values in state.
      logo.setStored(null);
      cover.setStored(null);
      onSaved && onSaved();
      setTimeout(dismissToast, 3200);
    } catch (err) {
      // Surface the real failure (which field, why) instead of a fake "saved".
      // eslint-disable-next-line no-console
      console.error('Brand profile save failed:', err?.status, err?.data || err);
      setErrorKey(profileErrorKey(err));
      setState('error');
      setTimeout(dismissToast, 4200);
    }
  }

  const disabled = !canEdit;
  const ibanLocked = disabled || support.canManageBilling !== true;
  const ibanHint = support.canManageBilling === false
    ? t('profile.ibanNeedsBilling')
    : support.canManageBilling === null && support.profileFields === false ? t('profile.notStoredYet') : null;

  return (
    <form className="panel-card" onSubmit={handleSave} data-testid="profile-tab">
      {/* Floating confirmation toast — a clearly visible save signal. */}
      {(state === 'saved' || state === 'error') && (
        <div
          className={`save-toast save-toast--${state} ${toastLeaving ? 'is-leaving' : ''}`}
          role="status"
          data-testid="profile-toast"
        >
          {state === 'saved' ? (
            <>
              <Check2 size={18} />
              <span>{t('profile.saved')}</span>
            </>
          ) : (
            <>
              <XLg size={16} />
              <span>{t(errorKey)}</span>
            </>
          )}
        </div>
      )}

      <h2 className="panel-card__title">{t('profile.title')}</h2>

      <CoverField src={cover.preview} onPick={disabled ? null : cover.pick} label={t('profile.cover')} hint={t('profile.coverHint')} />
      <LogoField src={logo.preview} name={name} onPick={disabled ? null : logo.pick} label={t('profile.logo')} />

      <Field label={t('profile.name')}>
        <input value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
      </Field>
      <Field label={t('profile.company')}>
        <input
          value={company}
          maxLength={PROFILE_COMPANY_MAX}
          onChange={(e) => setCompany(e.target.value)}
          disabled={disabled || support.profileFields === false}
          data-testid="profile-company"
        />
      </Field>
      <Field label={t('profile.tagline')}>
        <input
          value={tagline}
          maxLength={BRAND_TAGLINE_MAX}
          placeholder={t('profile.taglineHint')}
          onChange={(e) => setTagline(e.target.value)}
          disabled={disabled}
        />
      </Field>
      <Field label={t('profile.description')}>
        <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} disabled={disabled} />
      </Field>
      <div className="field-row">
        <Field label={t('profile.phone')}>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={disabled} />
        </Field>
        {/* The web address is set once, in Beállítások ("A webshop címe"):
            it is the site the fit-check key runs on. */}
        <div className="field">
          <span>{t('profile.website')}</span>
          <p className="field__hint" data-testid="profile-website-hint">
            {t('profile.websiteMoved')}{' '}
            <Link to={`${embedded ? '' : '/dashboard'}/embedded-settings#brand-profile`}>{t('profile.websiteLink')}</Link>
          </p>
        </div>
      </div>
      <Field label={t('profile.address')}>
        <input
          value={address}
          maxLength={PROFILE_ADDRESS_MAX}
          onChange={(e) => setAddress(e.target.value)}
          disabled={disabled || support.profileFields === false}
          data-testid="profile-address"
        />
      </Field>
      {support.profileFields === false && <p className="field__hint">{t('profile.notStoredYet')}</p>}
      <Field label={t('profile.iban')}>
        <input
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder="HU00 0000 0000 0000 0000 0000 0000"
          disabled={ibanLocked}
          autoComplete="off"
          spellCheck={false}
          data-testid="profile-iban"
        />
      </Field>
      {ibanHint && <p className="field__hint">{ibanHint}</p>}

      <label className={`toggle-field${suspended ? ' is-locked' : ''}`}>
        <input
          type="checkbox"
          checked={suspended ? false : isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          disabled={disabled || suspended}
          data-testid="profile-visibility"
        />
        <span>{t('profile.visibility')}</span>
      </label>
      {suspended && <p className="field__hint field__hint--error" data-testid="profile-visibility-locked">{t('profile.visibilitySuspended')}</p>}

      {canEdit && (
        <div className="panel-card__foot">
          <button type="submit" className="btn-o btn-o--dark" disabled={state === 'saving'}>
            {state === 'saving' ? t('profile.saving') : t('profile.save')}
          </button>
        </div>
      )}
      {!canEdit && <div className="panel-card__muted">{t('profile.readOnly')}</div>}
    </form>
  );
}

// ── Products tab ────────────────────────────────────────────────────────────

/**
 * Link to a product in the merchant's own Shopify admin.
 *
 * `shopify_product_id` is stored as the numeric REST id, but a GID
 * ("gid://shopify/Product/123") turns up in some sync paths — take the trailing
 * digits either way rather than assuming one shape.
 */
function shopifyProductUrl(shopDomain, shopifyProductId) {
  if (!shopDomain || !shopifyProductId) return null;
  const numeric = String(shopifyProductId).match(/(\d+)\s*$/);
  if (!numeric) return null;
  return `https://${shopDomain}/admin/products/${numeric[1]}`;
}

function productStock(p) {
  const variants = Array.isArray(p?.variants) ? p.variants : [];
  if (variants.length === 0) return { total: null, tracked: 0, soldOut: 0, variants: 0 };

  let total = 0;
  let tracked = 0;
  let soldOut = 0;

  for (const variant of variants) {
    const raw = variant?.inventory_quantity;
    const parsed = typeof raw === 'number'
      ? raw
      : (raw == null || raw === '' ? null : parseInt(raw, 10));
    if (parsed == null || Number.isNaN(parsed)) continue;
    tracked += 1;
    total += Math.max(0, parsed);
    if (parsed <= 0) soldOut += 1;
  }

  return tracked === 0
    ? { total: null, tracked: 0, soldOut: 0, variants: variants.length }
    : { total, tracked, soldOut, variants: variants.length };
}

/** First variant price, formatted in the product's own currency. */
function productPrice(p) {
  const raw = p.price ?? p.price_data?.price ?? p.variants?.[0]?.price;
  if (raw == null || raw === '') return null;

  const value = Number(raw);
  if (!Number.isFinite(value)) return null;

  const currency = p.currency || p.price_data?.currency;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'HUF').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    // Unknown currency code — never let formatting blank out the price.
    return `${value.toLocaleString()}${currency ? ` ${currency.toUpperCase()}` : ''}`;
  }
}

// How often the Products tab asks how the running sync is doing. The work is a
// Shopify fetch plus per-product AI classification, so it moves in seconds, not
// milliseconds — polling faster would only add load for a bar that cannot show
// the difference.
const SYNC_POLL_MS = 2000;
const SYNC_IDLE_POLL_MS = 10000;

const LOW_STOCK_THRESHOLD = 5;

// The product list in whichever envelope the two endpoints use: a bare array
// (portal) or `{ products: [...] }` (embedded, paged).
function productListOf(list) {
  return Array.isArray(list) ? list : list?.data ?? list?.products ?? [];
}

// `canRelabel` (documents#188): the FAILED badge's Retry needs editProducts —
// the same guard the relabel endpoint enforces.
// ── Website product crawl ("Termékek fetchelése") ──────────────────────────
//
// For brands that DON'T sync from Shopify: a manual fetch of the brand's OWN
// saved webshop (site_domain), ingested into the same catalogue the products
// grid shows. POST /brands/:id/products/crawl → 202 { jobId, … }, then
// GET …/crawl/:jobId polled to completion (see api/embed.js).
//
// The crawl registry is per-process and in-memory (api: crawl_jobs.js), so
// there is no server-side "last crawl" to read. We remember the last finished
// fetch locally, per brand, so "Utoljára fetchelve" survives a reload. It is a
// per-viewer convenience, never shared state — wrapped in try/catch because
// localStorage throws inside the Shopify admin iframe.
const CRAWL_POLL_MS = 1500;
const lastCrawlKey = (brandId) => `partner_last_crawl_${brandId}`;

function readLastCrawl(brandId) {
  try {
    const raw = localStorage.getItem(lastCrawlKey(brandId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeLastCrawl(brandId, record) {
  try {
    localStorage.setItem(lastCrawlKey(brandId), JSON.stringify(record));
  } catch {
    /* storage blocked — the line simply isn't remembered */
  }
}

// `onDone` fires when a crawl finishes with products, so the grid refetches.
function ProductCrawlControl({ brand, onNavigate, onDone }) {
  const { t } = useLocale();
  const fmt = useFormat();
  const [status, setStatus] = useState('idle'); // idle | running | done | error | unavailable
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [last, setLast] = useState(() => readLastCrawl(brand.id));
  const timer = useRef(null);

  useEffect(() => {
    setLast(readLastCrawl(brand.id));
    return () => clearTimeout(timer.current);
  }, [brand.id]);

  const recordFinished = useCallback((j) => {
    const record = {
      finishedAt: j?.finishedAt || new Date().toISOString(),
      found: j?.found ?? 0,
      ingested: j?.ingested ?? 0,
      failed: j?.failed ?? 0,
    };
    writeLastCrawl(brand.id, record);
    setLast(record);
  }, [brand.id]);

  const poll = useCallback((jobId) => {
    fetchCrawlStatus(brand.id, jobId)
      .then((j) => {
        setJob(j);
        if (j.status === 'running') {
          timer.current = setTimeout(() => poll(jobId), CRAWL_POLL_MS);
          return;
        }
        if (j.status === 'error' || (j.error && j.error !== 'NO_PRODUCTS')) {
          setStatus('error');
          setError(j.error || 'CRAWL_FAILED');
          return;
        }
        if (j.found === 0 || j.error === 'NO_PRODUCTS') {
          setStatus('error');
          setError('NO_PRODUCTS');
          recordFinished(j);
          return;
        }
        setStatus('done');
        recordFinished(j);
        onDone?.();
      })
      .catch(() => {
        setStatus('error');
        setError('CRAWL_FAILED');
      });
  }, [brand.id, onDone, recordFinished]);

  async function start() {
    setStatus('running');
    setError(null);
    setJob(null);
    try {
      const j = await startProductCrawl(brand.id);
      setJob(j);
      poll(j.jobId);
    } catch (err) {
      if (err instanceof CrawlUnavailable) {
        setStatus('unavailable');
        return;
      }
      setStatus('error');
      setError(err?.data?.error || 'CRAWL_FAILED');
    }
  }

  const running = status === 'running';
  // DOMAIN_NOT_SET is the only error with a route out: send them to Beüzemelés
  // where the webshop address is set.
  const errKey = ['NO_PRODUCTS', 'DOMAIN_NOT_SET'].includes(error) ? error : 'CRAWL_FAILED';

  return (
    <div className="product-crawl" data-testid="product-crawl">
      <div className="product-crawl__row">
        <button
          type="button"
          className="btn-o btn-o--brand"
          onClick={start}
          disabled={running || status === 'unavailable'}
          data-testid="product-crawl-start"
        >
          <ArrowRepeat size={15} className={running ? 'product-crawl__spin' : ''} aria-hidden="true" />
          {running ? t('products.crawling') : t('products.crawl')}
        </button>

        {status === 'unavailable' ? (
          <span className="product-crawl__meta product-crawl__meta--muted" data-testid="product-crawl-soon">
            {t('products.crawlSoon')}
          </span>
        ) : last ? (
          <span className="product-crawl__meta" data-testid="product-crawl-last">
            {t('products.lastCrawl', { ago: fmt.ago(last.finishedAt) })}
            {typeof last.ingested === 'number' && (
              <span className="product-crawl__last-count">
                {t('products.lastCrawlCount', { n: last.ingested })}
              </span>
            )}
          </span>
        ) : (
          <span className="product-crawl__meta product-crawl__meta--muted" data-testid="product-crawl-never">
            {t('products.lastCrawlNever')}
          </span>
        )}
      </div>

      {running && job && (
        <div className="product-crawl__progress" aria-live="polite" data-testid="product-crawl-progress">
          <span className="product-crawl__phase">
            {t(`products.crawlPhase.${['discovering', 'ingesting', 'done'].includes(job.phase) ? job.phase : 'queued'}`)}
          </span>
          <span className="product-crawl__nums">
            {t('products.crawlProgress', { found: job.found ?? 0, ingested: job.ingested ?? 0 })}
          </span>
        </div>
      )}

      {status === 'done' && job && (
        <div className="product-crawl__note product-crawl__note--ok" data-testid="product-crawl-done">
          <CheckCircleFill size={15} aria-hidden="true" />
          <span>{t('products.crawlDone', { n: job.ingested ?? job.found ?? 0 })}</span>
        </div>
      )}

      {status === 'error' && (
        <div className="product-crawl__note product-crawl__note--err" data-testid="product-crawl-error">
          <ExclamationCircleFill size={15} aria-hidden="true" />
          <span>
            {t(`products.crawlError.${errKey}`)}
            {errKey === 'DOMAIN_NOT_SET' && onNavigate && (
              <button
                type="button"
                className="product-crawl__link"
                onClick={() => onNavigate('onboarding')}
                data-testid="product-crawl-setdomain"
              >
                {t('products.crawlSetDomain')}
              </button>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

function ProductsTab({ brand, canSync, canCrawl = false, canRelabel = false, onNavigate }) {
  const { t } = useLocale();
  const { user } = useAuth();
  const devBrand = user?.environment === 'dev' && brand.environment === 'dev';
  const [products, setProducts] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // #615 — the live sync counts, or null when nothing is running.
  const [progress, setProgress] = useState(null);
  // Bumped when a sync is kicked off, to restart the poll loop below.
  const [pollNonce, setPollNonce] = useState(0);
  const [disputes, setDisputes] = useState({});
  const [disputesNonce, setDisputesNonce] = useState(0);

  const visibleProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!products || !q) return products;
    return products.filter((p) => (p.name || p.title || '').toLowerCase().includes(q));
  }, [products, query]);

  // #615 — the catalogue splits in two. Anything the AI classifier did not
  // call clothing is still the merchant's product and still synced, it just
  // never reaches the shopper-facing surfaces; showing it below the fold with
  // the reason attached beats leaving them to wonder what happened to it.
  //
  // Only an explicit `false` demotes a product: a null or missing is_clothing
  // means "not classified", which is not the same claim.
  const [clothing, nonClothing] = useMemo(() => {
    const included = [];
    const excluded = [];
    for (const p of visibleProducts ?? []) {
      (p.is_clothing === false ? excluded : included).push(p);
    }
    return [included, excluded];
  }, [visibleProducts]);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    fetchBrandProducts(brand.id)
      .then((list) => {
        if (alive) setProducts(productListOf(list));
      })
      // A failed request used to render as "no products yet" — indistinguishable
      // from an empty catalogue, so the merchant re-syncs and nothing changes.
      // Say the load failed and offer the retry.
      .catch(() => {
        if (!alive) return;
        setProducts([]);
        setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, [brand.id, reloadKey]);

  // Watches a running sync and retires itself when there is nothing to watch.
  //
  // Runs on mount too, not only after the sync button: a merchant who reloads
  // the tab mid-sync, or who started one from the embedded Shopify app, should
  // still see the bar.
  useEffect(() => {
    let alive = true;
    let timer = null;
    // Only a run we actually saw in flight should refresh the grid when it
    // ends. Without this, a 'completed' left over from an earlier sync would
    // trigger a pointless refetch on every mount.
    let sawSyncing = false;

    let inFlight = false;

    async function poll() {
      timer = null;
      let next = null;
      inFlight = true;
      try {
        next = await fetchSyncProgress(brand.id);
      } catch {
        // The bar is decoration. A failed poll takes it down; it must
        // never turn a working catalogue into an error screen.
        inFlight = false;
        if (!alive) return;
        setProgress(null);
        if (!document.hidden) timer = setTimeout(poll, SYNC_IDLE_POLL_MS);
        return;
      }
      inFlight = false;
      if (!alive) return;

      if (next?.status === 'syncing') {
        sawSyncing = true;
        setProgress(next);
        timer = setTimeout(poll, SYNC_POLL_MS);
        return;
      }

      setProgress(null);
      if (sawSyncing) {
        sawSyncing = false;
        setReloadKey((n) => n + 1);
      }
      if (!document.hidden) timer = setTimeout(poll, SYNC_IDLE_POLL_MS);
    }

    function onVisibilityChange() {
      if (document.hidden || !alive || inFlight) return;
      if (timer) clearTimeout(timer);
      poll();
    }

    poll();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [brand.id, pollNonce]);

  // documents#188 — while any product is still being labelled, re-read the
  // list every 5 s so the PENDING badges resolve without a manual refresh.
  // Only then: an idle catalogue is never polled. Paused while a sync runs
  // (the grid is hidden behind the progress bar, and the sync's own refetch
  // follows). A quiet re-read: the grid stays up, a failed poll changes
  // nothing — the tab must never flicker or blank because of it.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const labelingPending = useMemo(() => hasPendingLabeling(products), [products]);
  useLabelingPoll({
    active: labelingPending && !progress,
    intervalMs: LABELING_POLL_MS,
    onTick: () => {
      fetchBrandProducts(brand.id)
        .then((list) => { if (mounted.current) setProducts(productListOf(list)); })
        .catch(() => {});
    },
  });

  // A relabel was accepted (202): the response carries the queued block, so
  // the card reads PENDING at once and the poll above takes it from there.
  function handleRelabeled(productId, aiLabeling) {
    setProducts((list) => withLabeling(list, productId, aiLabeling));
  }

  useEffect(() => {
    let alive = true;
    fetchLabelDisputes(brand.id)
      .then((list) => { if (alive) setDisputes(latestDisputesByProduct(list)); })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [brand.id, disputesNonce]);

  function handleDisputed(productId, dispute) {
    setDisputes((map) => ({ ...map, [String(productId)]: dispute }));
    setDisputesNonce((n) => n + 1);
  }

  async function handleSync() {
    setSyncing(true);
    try {
      await syncProducts(brand.id);
      // The POST only starts the run — the worker is still asking Shopify for
      // the catalogue — so the first polls may come back with no total yet.
      // From here the progress bar is the sync's only report; there is no
      // "started" line to read and then have to dismiss.
      setPollNonce((n) => n + 1);
    } catch {
      // As before: if the sync will not start, the bar simply never appears.
    } finally {
      setSyncing(false);
    }
  }

  // One product card. Both grids render the same thing — the excluded list
  // below differs in its wrapper and its opacity, never in what a product
  // looks like.
  function renderCell(p, i) {
    const img = (p.images && (p.images[0]?.src || p.images[0])) || p.image || null;
    const price = productPrice(p);
    const adminUrl = shopifyProductUrl(brand.website, p.shopify_product_id);
    const status = p.shopify_status || p.product_status;
    const inactive = brand.isActive === false || brand.adminSuspended === true || String(status || '').toUpperCase() === 'BRAND_INACTIVE';
    const stock = productStock(p);
    const out = stock.total === 0;
    const low = !out && stock.total !== null && stock.total <= LOW_STOCK_THRESHOLD;

    return (
      <div className={`product-cell${out ? ' is-sold-out' : ''}`} key={p.id || i}>
        <div className="product-cell__img">
          {img ? <img src={img} alt="" loading="lazy" /> : <span aria-hidden="true">▢</span>}
          {inactive ? (
            <span className="product-cell__status is-inactive">{t('products.inactive')}</span>
          ) : status && status !== 'ACTIVE' && (
            <span className="product-cell__status">{status}</span>
          )}
          {out && <span className="product-cell__sold-out">{t('products.soldOut')}</span>}
          {devBrand && <span className="product-cell__dev">{t('shell.envDev')}</span>}
        </div>

        <div className="product-cell__body">
          <div className="product-cell__name" title={p.name || p.title || ''}>
            {p.name || p.title || '—'}
          </div>
          {price && <div className="product-cell__price">{price}</div>}
          <div className={`product-cell__stock${out ? ' is-out' : ''}${low ? ' is-low' : ''}`}>
            {stock.total === null
              ? t('products.stockUntracked')
              : t('products.stockCount', { n: stock.total })}
            {stock.variants > 1 && stock.soldOut > 0 && stock.total !== null && (
              <span className="product-cell__stock-variants">
                {t('products.stockVariantsOut', { out: stock.soldOut, total: stock.variants })}
              </span>
            )}
          </div>
          {/* documents#188 — AI labelling status; nothing for a never-queued row. */}
          <LabelingBadge
            product={p}
            brandId={brand.id}
            canRelabel={canRelabel}
            onRelabeled={handleRelabeled}
          />
          <LabelDispute
            product={p}
            brandId={brand.id}
            dispute={disputes[String(p.id)]}
            canDispute={canRelabel}
            onDisputed={handleDisputed}
          />
        </div>

        {adminUrl && (
          <div className="product-cell__foot">
            <a
              className="product-cell__link"
              href={adminUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{t('products.openInShopify')}</span>
              <BoxArrowUpRight size={12} />
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">
          {t('products.title')}
          {products?.length > 0 && <span className="product-count">{products.length}</span>}
        </h2>
        {canSync && (
          <button className="btn-o btn-o--brand" onClick={handleSync} disabled={syncing}>
            {syncing ? t('products.syncing') : t('products.sync')}
          </button>
        )}
      </div>
      {/* Website product crawl — only for brands that don't sync from Shopify.
          A manual fetch of the brand's own saved webshop, with live progress
          and "Utoljára fetchelve". Hidden while a Shopify sync bar owns the
          panel below. */}
      {canCrawl && !progress && (
        <ProductCrawlControl
          brand={brand}
          onNavigate={onNavigate}
          onDone={() => setReloadKey((n) => n + 1)}
        />
      )}
      {/* #615 — while a sync runs the bar is the whole panel. What is in the
          grid is the catalogue as it was BEFORE this run, so showing it next to
          a bar that says it is being rewritten offers the merchant a list they
          cannot trust. Until the worker has heard back from Shopify there is no
          total to divide by either, so the bar says it is preparing rather than
          guessing at a length. */}
      {progress ? (
        <div className="sync-progress sync-progress--solo">
          <div className="sync-progress__head">
            <span className="sync-progress__label">{t('products.syncProgress')}</span>
            <span className="sync-progress__count">
              {progress.total
                ? t('products.syncCount', {
                    done: progress.processed,
                    total: progress.total,
                  })
                : t('products.syncPreparing')}
            </span>
          </div>
          <div
            className={`sync-progress__track${progress.total ? '' : ' is-indeterminate'}`}
            role="progressbar"
            aria-label={t('products.syncProgress')}
            aria-valuemin={0}
            {...(progress.total
              ? { 'aria-valuemax': progress.total, 'aria-valuenow': progress.processed }
              : {})}
          >
            <div
              className="sync-progress__fill"
              style={
                progress.total
                  ? { width: `${Math.round((progress.processed / progress.total) * 100)}%` }
                  : undefined
              }
            />
          </div>
        </div>
      ) : (
        <>
          {products?.length > 0 && (
            <div className="product-search">
              <Search size={14} className="product-search__icon" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('products.search')}
                aria-label={t('products.search')}
              />
            </div>
          )}

          {products === null ? (
            <div className="panel-card__muted">{t('shell.loading')}</div>
          ) : loadError ? (
            <div className="panel-card__muted">
              {t('products.loadError')}{' '}
              <button
                className="btn-o btn-o--ghost btn-o--sm"
                onClick={() => setReloadKey((n) => n + 1)}
              >
                {t('products.retry')}
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="panel-card__muted">
              {canCrawl ? t('products.emptyCrawl') : t('products.empty')}
            </div>
          ) : visibleProducts.length === 0 ? (
            <div className="panel-card__muted">{t('products.noMatch')}</div>
          ) : (
            <>
              {clothing.length > 0 && (
                <div className="product-grid">{clothing.map(renderCell)}</div>
              )}

              {/* #615 — everything the classifier did not call clothing, kept out
                  of the catalogue above but not out of sight. Dimmed and below a
                  rule, because it is still the merchant's product and still
                  synced; it just never reaches a shopper. */}
              {nonClothing.length > 0 && (
                <section className="excluded">
                  <h3 className="excluded__title">
                    {t('products.excludedTitle')}
                    <span className="product-count">{nonClothing.length}</span>
                  </h3>
                  <p className="excluded__hint">{t('products.excludedHint')}</p>
                  <div className="product-grid excluded__grid">
                    {nonClothing.map(renderCell)}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
// ── Creator campaigns tab (REAL — the brand's briefs, applicants, new brief) ─
//
// This is the brand side of the creator collaboration, wired to the live
// backend (/brands/:brandId/campaigns …). Three views: list → detail
// (applicants) → new. All endpoints require the `manageCampaigns` permission.

function fmtFollowers(n) {
  if (n == null) return '';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function CampaignsTab({ brand, canEdit }) {
  const { t } = useLocale();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list'); // list | detail | new
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  function loadList() {
    setLoading(true);
    setError('');
    fetchBrandCampaigns(brand.id)
      .then((list) => setCampaigns(Array.isArray(list) ? list : []))
      .catch((e) => setError(e?.message || t('campaigns.error')))
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  function openDetail(id) {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setView('detail');
    fetchBrandCampaign(brand.id, id)
      .then((c) => setDetail(c))
      .catch((e) => setError(e?.message || t('campaigns.error')))
      .finally(() => setDetailLoading(false));
  }

  async function handleCreate(draft, setFormError) {
    try {
      await createBrandCampaign(brand.id, draft);
      setView('list');
      loadList();
    } catch (e) {
      if (setFormError) setFormError(e?.message || t('campaigns.error'));
    }
  }

  // Select / reject a creator for a brief, then refetch the detail.
  async function toggleSelect(campaignId, userId, currentlySelected, onError) {
    try {
      if (currentlySelected) {
        await rejectCampaignApplicant(brand.id, campaignId, userId);
      } else {
        await selectCampaignApplicant(brand.id, campaignId, userId);
      }
      const fresh = await fetchBrandCampaign(brand.id, campaignId);
      setDetail(fresh);
      loadList();
    } catch (e) {
      // 409 → slots full; surface the server message.
      if (onError) onError(e?.message || t('campaigns.error'));
    }
  }

  if (view === 'new') {
    return (
      <NewCampaignForm
        onCancel={() => setView('list')}
        onCreate={handleCreate}
      />
    );
  }

  if (view === 'detail') {
    return (
      <CampaignDetail
        campaign={detail}
        loading={detailLoading}
        canEdit={canEdit}
        onBack={() => setView('list')}
        onToggleSelect={(userId, currentlySelected, onError) =>
          toggleSelect(selectedId, userId, currentlySelected, onError)
        }
      />
    );
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">{t('campaigns.title')}</h2>
        {canEdit && (
          <button className="btn-o btn-o--brand" onClick={() => setView('new')}>
            <PlusLg size={15} style={{ marginRight: 6 }} />
            {t('campaigns.new')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="panel-card__muted">{t('campaigns.loading')}</div>
      ) : error ? (
        <div className="panel-card__muted">{error}</div>
      ) : campaigns.length === 0 ? (
        <div className="panel-card__muted">{t('campaigns.empty')}</div>
      ) : (
        <div className="campaign-list">
          {campaigns.map((c) => {
            const applicants = c.applicants || [];
            const selectedCount = applicants.filter((a) => a.selected).length;
            return (
              <button
                key={c.id}
                className="campaign-row"
                onClick={() => openDetail(c.id)}
              >
                <div className="campaign-row__main">
                  <div className="campaign-row__title">{c.title}</div>
                  <div className="campaign-row__meta">
                    <span className={`campaign-row__status is-${c.status}`}>
                      {t(`campaigns.status.${c.status}`)}
                    </span>
                    <span className="campaign-row__dot">·</span>
                    <span>
                      {t('campaigns.applicantsCount', { count: applicants.length })}
                    </span>
                    <span className="campaign-row__dot">·</span>
                    <span>
                      {t('campaigns.selectedCount', {
                        done: selectedCount,
                        total: c.slotsNeeded,
                      })}
                    </span>
                  </div>
                </div>
                <ChevronRight size={18} className="campaign-row__chev" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CampaignDetail({ campaign, loading, canEdit, onBack, onToggleSelect }) {
  const { t } = useLocale();
  const [actionError, setActionError] = useState('');

  if (loading || !campaign) {
    return (
      <div className="panel-card">
        <button className="link-back" onClick={onBack}>
          ‹ {t('campaigns.back')}
        </button>
        <div className="panel-card__muted">{t('campaigns.loading')}</div>
      </div>
    );
  }

  const applicants = campaign.applicants || [];
  const selectedCount = applicants.filter((a) => a.selected).length;

  return (
    <div className="panel-card">
      <button className="link-back" onClick={onBack}>
        ‹ {t('campaigns.back')}
      </button>

      <div className="panel-card__row">
        <h2 className="panel-card__title">{campaign.title}</h2>
        <span className={`campaign-row__status is-${campaign.status}`}>
          {t(`campaigns.status.${campaign.status}`)}
        </span>
      </div>

      <p className="campaign-detail__desc">{campaign.description}</p>

      <div className="campaign-detail__facts">
        <span className="campaign-detail__fact">
          <Calendar3 size={14} />
          {t('campaigns.applyDeadline')}: {campaign.applyDeadline}
        </span>
        <span className="campaign-detail__fact">
          <Calendar3 size={14} />
          {t('campaigns.deliveryDeadline')}: {campaign.deliveryDeadline}
        </span>
        <span className="campaign-detail__fact">
          {t('campaigns.slots', { count: campaign.slotsNeeded })}
        </span>
        <span className="campaign-detail__fact">
          {t('campaigns.videos', { count: campaign.requiredVideos })}
        </span>
      </div>

      <div className="campaign-detail__applicants-head">
        <span>{t('campaigns.applicants')}</span>
        <span className="campaign-detail__selected">
          {t('campaigns.selectedCount', {
            done: selectedCount,
            total: campaign.slotsNeeded,
          })}
        </span>
      </div>

      {actionError && (
        <div className="panel-card__muted">{actionError}</div>
      )}

      {applicants.length === 0 ? (
        <div className="panel-card__muted">{t('campaigns.noApplicants')}</div>
      ) : (
        <div className="applicant-list">
          {applicants.map((a) => (
            <div
              className={`applicant-row ${a.selected ? 'is-selected' : ''}`}
              key={a.userId}
            >
              <span className="applicant-row__avatar">
                {(a.username || '?')[0].toUpperCase()}
              </span>
              <div className="applicant-row__id">
                <div className="applicant-row__name">@{a.username}</div>
                <div className="applicant-row__sub">
                  {fmtFollowers(a.followers)} · {a.platform}
                </div>
              </div>
              {a.selected && (
                <span className="applicant-row__badge">
                  <Check2 size={13} /> {t('campaigns.selected')}
                </span>
              )}
              {canEdit && (
                <button
                  className={`btn-o btn-o--sm ${a.selected ? 'btn-o--ghost' : 'btn-o--dark'}`}
                  onClick={() => {
                    setActionError('');
                    onToggleSelect(a.userId, a.selected, setActionError);
                  }}
                >
                  {a.selected ? t('campaigns.deselect') : t('campaigns.select')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewCampaignForm({ onCancel, onCreate }) {
  const { t } = useLocale();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slotsNeeded, setSlotsNeeded] = useState(3);
  const [requiredVideos, setRequiredVideos] = useState(1);
  const [payment, setPayment] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [applyDeadline, setApplyDeadline] = useState('');
  const [deliveryDeadline, setDeliveryDeadline] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const valid = title.trim() && description.trim();

  function submit(e) {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    Promise.resolve(
      onCreate(
        {
          title: title.trim(),
          description: description.trim(),
          slotsNeeded: Number(slotsNeeded) || 1,
          requiredVideos: Number(requiredVideos) || 1,
          payment: payment.trim(),
          amount: amount === '' ? null : Number(amount),
          currency,
          applyDeadline,
          deliveryDeadline,
        },
        setError
      )
    ).finally(() => setSubmitting(false));
  }

  return (
    <form className="panel-card" onSubmit={submit}>
      <button type="button" className="link-back" onClick={onCancel}>
        ‹ {t('campaigns.back')}
      </button>
      <h2 className="panel-card__title">{t('campaigns.newTitle')}</h2>

      <Field label={t('campaigns.fTitle')}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={t('campaigns.fDescription')}>
        <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="field-row">
        <Field label={t('campaigns.fSlots')}>
          <input type="number" min="1" value={slotsNeeded} onChange={(e) => setSlotsNeeded(e.target.value)} />
        </Field>
        <Field label={t('campaigns.fVideos')}>
          <input type="number" min="1" value={requiredVideos} onChange={(e) => setRequiredVideos(e.target.value)} />
        </Field>
      </div>
      <Field label={t('campaigns.fPayment')}>
        <input value={payment} onChange={(e) => setPayment(e.target.value)} placeholder={t('campaigns.fPaymentPh')} />
      </Field>
      <div className="field-row">
        <Field label={t('campaigns.fAmount')}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('campaigns.fAmountPh')}
          />
        </Field>
        <Field label={t('campaigns.fCurrency')}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="EUR">EUR</option>
            <option value="HUF">HUF</option>
          </select>
        </Field>
      </div>
      <div className="field-row">
        <Field label={t('campaigns.applyDeadline')}>
          <input type="date" value={applyDeadline} onChange={(e) => setApplyDeadline(e.target.value)} />
        </Field>
        <Field label={t('campaigns.deliveryDeadline')}>
          <input type="date" value={deliveryDeadline} onChange={(e) => setDeliveryDeadline(e.target.value)} />
        </Field>
      </div>

      {error && <div className="panel-card__muted">{error}</div>}

      <div className="panel-card__foot">
        <button type="submit" className="btn-o btn-o--dark" disabled={!valid || submitting}>
          {submitting ? t('campaigns.creating') : t('campaigns.create')}
        </button>
        <button type="button" className="btn-o btn-o--ghost" onClick={onCancel}>
          {t('campaigns.cancel')}
        </button>
      </div>
    </form>
  );
}

// ── Stats tab (real: overview + revenue + payouts) ──────────────────────────

function StatsTab({ brand }) {
  const { t } = useLocale();
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchStatsOverview(brand.id).then((d) => d?.success && setOverview(d)).catch(() => {});
  }, [brand.id]);

  useEffect(() => {
    fetchRevenue(brand.id, period).then((d) => d?.success && setRevenue(d)).catch(() => {});
  }, [brand.id, period]);

  const cur = (n) =>
    n == null ? '—' : `${Number(n).toLocaleString()} ${(overview?.currency || '').toUpperCase()}`;

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">{t('stats.title')}</h2>

      {/* Balance / earnings */}
      <div className="stat-cards">
        <StatCard label={t('stats.available')} value={cur(overview?.balance?.available)} />
        <StatCard label={t('stats.totalEarned')} value={cur(overview?.balance?.total_earned)} />
        <StatCard label={t('stats.thisMonth')} value={cur(overview?.this_month?.revenue)} />
        <StatCard label={t('stats.orders')} value={overview?.this_month?.orders ?? '—'} />
      </div>

      {/* Revenue period selector + total */}
      <div className="stat-block">
        <div className="stat-block__head">
          <span>{t('stats.revenue')}</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {['day', 'week', 'month', 'year', 'all'].map((p) => (
              <option key={p} value={p}>
                {t(`stats.period.${p}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="stat-block__big">{cur(revenue?.stats?.total_revenue)}</div>
        {revenue?.chart_data?.length > 0 && (
          <MiniChart data={revenue.chart_data} />
        )}
      </div>
    </div>
  );
}


// ── Orders tab — what this brand has to fulfil ───────────────────────────────
// A safety net beside the Shopify store, not a replacement for it. The Shopify
// order is where a merchant actually packs and ships; this is where they can
// check what Outfino recorded — including on staging, where no Shopify order is
// created at all and the store would otherwise look empty after a real purchase.
//
// Every figure here is this brand's own slice. An order can contain another
// brand's products; those never appear.
function OrdersTab({ brand }) {
  const { t } = useLocale();
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    fetchBrandOrders(brand.id)
      .then((d) => {
        if (!alive) return;
        setOrders(d?.orders ?? d?.message?.orders ?? []);
      })
      .catch(() => {
        if (!alive) return;
        setOrders([]);
        setLoadError(true);
      });
    return () => { alive = false; };
  }, [brand.id]);

  function money(value, currency) {
    if (value == null) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'HUF').toUpperCase(),
        maximumFractionDigits: 0,
      }).format(n);
    } catch (e) {
      return `${n.toLocaleString()} ${currency || ''}`.trim();
    }
  }

  function when(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (e) {
      return '—';
    }
  }

  function addressLines(address) {
    if (!address || typeof address !== 'object') return [];
    return [
      address.customerName || address.customer_name,
      address.address1,
      [address.zip, address.city].filter(Boolean).join(' '),
      address.country,
    ].filter((v) => v && String(v).trim() !== '');
  }

  if (orders === null) {
    return <div className="panel-card panel-card__muted">{t('orders.loading')}</div>;
  }

  if (loadError) {
    return <div className="panel-card panel-card__muted">{t('orders.loadError')}</div>;
  }

  if (orders.length === 0) {
    return <div className="panel-card panel-card__muted">{t('orders.empty')}</div>;
  }

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">
        {t('orders.title')}
        <span className="product-count">{orders.length}</span>
      </h2>
      <p className="orders__hint">{t('orders.hint')}</p>

      <div className="orders">
        {orders.map((o) => {
          const open = openId === o.order_id;
          const units = o.items.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0);

          return (
            <div key={o.order_id} className={`order ${open ? 'is-open' : ''}`}>
              <button
                type="button"
                className="order__head"
                onClick={() => setOpenId(open ? null : o.order_id)}
              >
                <span className="order__number">{o.order_number}</span>
                <span className="order__meta">
                  {when(o.placed_at)} · {t('orders.units').replace('{n}', String(units))}
                </span>
                <span className="order__amount">{money(o.brand_amount, o.currency)}</span>
              </button>

              {open && (
                <div className="order__body">
                  <div className="order__items">
                    {o.items.map((i, idx) => (
                      <div key={`${o.order_id}-${idx}`} className="order__item">
                        {i.image
                          ? <img className="order__thumb" src={i.image} alt="" />
                          : <span className="order__thumb order__thumb--ph" />}
                        <span className="order__item-name">{i.name || '—'}</span>
                        <span className="order__item-qty">{i.quantity}×</span>
                        <span className="order__item-price">
                          {money((Number(i.price) || 0) * (Number(i.quantity) || 1), i.currency || o.currency)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <dl className="order__facts">
                    <dt>{t('orders.subtotal')}</dt>
                    <dd>{money(o.product_subtotal, o.currency)}</dd>

                    <dt>{t('orders.shipping')}</dt>
                    <dd>{money(o.shipping_cost, o.currency)}</dd>

                    {/* The brand's take after commission — the number that
                        reaches their balance, stated so they never have to
                        work it out from the subtotal and a percentage. */}
                    <dt>{t('orders.yourShare').replace('{n}', String(o.commission_percentage))}</dt>
                    <dd className="order__share">{money(o.brand_amount, o.currency)}</dd>

                    {o.shipping_method && (
                      <>
                        <dt>{t('orders.method')}</dt>
                        <dd>{o.shipping_method}</dd>
                      </>
                    )}

                    {o.shipping_attributes?.map((a) => (
                      <Fragment key={a.key}>
                        <dt>{a.key}</dt>
                        <dd>{a.value}</dd>
                      </Fragment>
                    ))}

                    <dt>{t('orders.deliverTo')}</dt>
                    <dd>
                      {addressLines(o.shipping_address).map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                      {o.customer_email && <div className="order__muted">{o.customer_email}</div>}
                      {o.customer_phone && <div className="order__muted">{o.customer_phone}</div>}
                    </dd>

                    <dt>{t('orders.shopifyOrder')}</dt>
                    <dd>
                      {o.shopify_order_id
                        ? <span className="order__mono">{o.shopify_order_id}</span>
                        : (
                          <>
                            <span className="order__muted">{t('orders.noShopifyOrder')}</span>
                            {o.shopify_note && <div className="order__mono">{o.shopify_note}</div>}
                          </>
                        )}
                    </dd>
                  </dl>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ── Payouts tab (available balance + payout history + request) ──────────────

function PayoutsTab({ brand }) {
  const { t } = useLocale();
  const [overview, setOverview] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState('');

  function loadPayouts() {
    fetchPayouts(brand.id).then((d) => d?.success && setPayouts(d)).catch(() => {});
  }
  useEffect(() => {
    fetchStatsOverview(brand.id).then((d) => d?.success && setOverview(d)).catch(() => {});
    loadPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  const currency = (
    payouts?.currency ||
    overview?.currency ||
    ''
  ).toUpperCase();
  const cur = (n) =>
    n == null ? '—' : `${Number(n).toLocaleString()} ${currency}`.trim();

  // Available balance: prefer the overview's balance, fall back to payouts.
  const available =
    overview?.balance?.available ?? payouts?.current_balance ?? null;
  const canRequest = Number(available) > 0 && !requesting;

  // Commission %. The API's amounts (brand_amount / total_earned / balance) are
  // already NET — i.e. after Outfino's commission is deducted. We reconstruct the
  // gross lifetime revenue and the commission taken so the split is transparent:
  //   net = gross * (1 - c/100)  ⇒  gross = net / (1 - c/100)
  const commissionPct = overview?.commission_percentage ?? null;
  const netEarned =
    overview?.balance?.total_earned ?? payouts?.total_earned ?? null;
  const hasSplit =
    commissionPct != null && commissionPct > 0 && commissionPct < 100 && netEarned != null;
  const grossEarned = hasSplit ? netEarned / (1 - commissionPct / 100) : null;
  const commissionTaken = hasSplit ? grossEarned - netEarned : null;

  async function handleRequest() {
    if (!canRequest) return;
    setRequesting(true);
    setNotice('');
    try {
      await requestPayout(brand.id, Number(available));
      setNotice(t('payouts.requested'));
      loadPayouts();
      fetchStatsOverview(brand.id).then((d) => d?.success && setOverview(d)).catch(() => {});
    } catch {
      setNotice(t('payouts.requestError'));
    } finally {
      setRequesting(false);
    }
  }

  const recent = payouts?.recent_payouts || [];

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">{t('payouts.title')}</h2>

      {/* Available balance hero + request button */}
      <div className="balance-hero">
        <div className="balance-hero__main">
          <div className="balance-hero__label">{t('stats.available')}</div>
          <div className="balance-hero__value">{cur(available)}</div>
          {commissionPct != null && commissionPct > 0 && (
            <div className="balance-hero__note">
              {t('payouts.commissionNote', { pct: commissionPct })}
            </div>
          )}
        </div>
        <button
          className="btn-o btn-o--brand"
          onClick={handleRequest}
          disabled={!canRequest}
        >
          {requesting ? t('payouts.requesting') : t('payouts.request')}
        </button>
      </div>
      {notice && <div className="panel-card__ok">{notice}</div>}

      {/* Revenue breakdown + pending, side by side */}
      <div className="payout-cols">
        {hasSplit && (
          <div className="breakdown">
            <div className="breakdown__title">{t('payouts.breakdownTitle')}</div>
            <div className="breakdown__row">
              <span>{t('payouts.gross')}</span>
              <span className="breakdown__amount">{cur(grossEarned)}</span>
            </div>
            <div className="breakdown__row breakdown__row--minus">
              <span>{t('payouts.commissionLine', { pct: commissionPct })}</span>
              <span className="breakdown__amount">− {cur(commissionTaken)}</span>
            </div>
            <div className="breakdown__row breakdown__row--net">
              <span>{t('payouts.net')}</span>
              <span className="breakdown__amount">{cur(netEarned)}</span>
            </div>
          </div>
        )}
        <div className="pending-card">
          <div className="breakdown__title">{t('payouts.pending')}</div>
          <div className="pending-card__value">{cur(payouts?.pending_payouts)}</div>
        </div>
      </div>

      {/* Payout history */}
      <div className="stat-block">
        <div className="stat-block__head">
          <span>{t('payouts.history')}</span>
        </div>
        {recent.length > 0 ? (
          <div className="payout-list">
            {recent.map((p) => (
              <div className="payout-row" key={p.id}>
                <span className="payout-row__amount">{cur(p.amount)}</span>
                <span className={`payout-row__status is-${p.status}`}>{p.status}</span>
                <span className="payout-row__date">
                  {p.requested_at ? new Date(p.requested_at).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="panel-card__muted">{t('stats.noPayouts')}</div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
    </div>
  );
}

// A tiny inline bar chart (no chart lib needed).
function MiniChart({ data }) {
  const max = Math.max(...data.map((d) => Number(d.revenue) || 0), 1);
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div
          key={i}
          className="mini-chart__bar"
          style={{ height: `${Math.max(4, (Number(d.revenue) / max) * 100)}%` }}
          title={`${d.revenue}`}
        />
      ))}
    </div>
  );
}

// ── Members tab ─────────────────────────────────────────────────────────────

// `readOnly` is the embedded case: adding a member needs an Outfino account to
// attach to, and the embedded app has no Outfino identity to search users with.
function MembersTab({ brand, currentUserId, isAdmin, readOnly = false }) {
  const { t } = useLocale();
  const [members, setMembers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  const { embedded, user } = useAuth();
  const devLocked = brand.environment === 'dev' || user?.environment === 'dev';
  const canAdd = !readOnly && !devLocked;

  function load() {
    fetchBrandMembers(brand.id)
      .then((list) => setMembers(Array.isArray(list) ? list : list?.data ?? []))
      .catch(() => setMembers([]));
  }
  useEffect(load, [brand.id]);

  // Ids already in the brand, so search results can hide existing members.
  const existingIds = useMemo(
    () =>
      new Set(
        (members || []).map((m) => m.user?.id || m.userId || m.user_id)
      ),
    [members]
  );

  const canEditPerms =
    !readOnly &&
    (embedded ||
      isAdmin ||
      (members || []).some(
        (m) => (m.user?.id || m.userId || m.user_id) === currentUserId && m.role === 'owner'
      ));

  function permsOf(m) {
    const p = m.permissions;
    if (Array.isArray(p)) return p;
    if (p && typeof p === 'object') return Object.keys(p).filter((k) => p[k]);
    return [];
  }

  async function togglePerm(m, key) {
    const uid = m.user?.id || m.userId || m.user_id;
    const cur = new Set(permsOf(m));
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    const next = [...cur];
    setBusy(uid);
    try {
      await updateBrandMember(brand.id, uid, next, 'member');
      load();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  }

  async function remove(m) {
    const uid = m.user?.id || m.userId || m.user_id;
    setBusy(uid);
    try {
      await removeBrandMember(brand.id, uid);
      load();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  }

  async function handleAdd(u) {
    // New members start with view-only access; the owner grants more via chips.
    const uid = u.id;
    try {
      await addBrandMember(brand.id, uid, ['viewProducts'], 'member');
      setAdding(false);
      load();
    } catch {
      /* ignore — likely already a member */
    }
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">{t('members.title')}</h2>
        {canAdd && (
          <button
            className="btn-o btn-o--dark"
            onClick={() => setAdding((v) => !v)}
          >
            <PersonPlus size={15} style={{ marginRight: 6 }} />
            {t('members.add')}
          </button>
        )}
      </div>

      {devLocked && !readOnly && (
        <div className="panel-card__muted">{t('members.devLocked')}</div>
      )}

      {canAdd && adding && (
        <AddMemberBox
          existingIds={existingIds}
          onPick={handleAdd}
          onClose={() => setAdding(false)}
        />
      )}

      {members === null ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : members.length === 0 ? (
        <div className="panel-card__muted">{t('members.empty')}</div>
      ) : (
        <div className="member-list">
          {members.map((m) => {
            const u = m.user || m;
            const uid = u.id || m.userId || m.user_id;
            const isOwner = m.role === 'owner';
            const mine = permsOf(m);
            // Mirror the mobile app's User.displayUsername: show `@username`,
            // or the localized "Névtelen"/"Anonymous" fallback — never first_name.
            const username = u.username || u.userName || u.handle || null;
            const display = username ? `@${username}` : t('members.anonymous');
            const initial = (username || '?')[0].toUpperCase();
            return (
              <div className="member-row" key={uid}>
                <div className="member-row__id">
                  {u.avatarUrl ? (
                    <img className="member-row__avatar" src={u.avatarUrl} alt="" />
                  ) : (
                    <span className="member-row__avatar member-row__avatar--ph">
                      {initial}
                    </span>
                  )}
                  <div>
                    <div className="member-row__name">
                      {display}
                      <span className={`member-row__owner${isOwner ? '' : ' member-row__owner--member'}`}>
                        {t(isOwner ? 'members.role.owner' : 'members.role.member')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="member-row__perms">
                  {MEMBER_PERMISSIONS.map((p) =>
                    isOwner ? (
                      <span key={p} className="perm-chip is-on is-locked">
                        {t(`perm.${p}`)}
                      </span>
                    ) : (
                      <button
                        key={p}
                        className={`perm-chip ${mine.includes(p) ? 'is-on' : ''}`}
                        disabled={!canEditPerms || busy === uid}
                        onClick={() => (canEditPerms ? togglePerm(m, p) : undefined)}
                      >
                        {t(`perm.${p}`)}
                      </button>
                    )
                  )}
                </div>

                {(!isOwner || isAdmin) && uid !== currentUserId && (
                  <button
                    className="member-row__remove"
                    disabled={readOnly || busy === uid}
                    onClick={() => (readOnly ? undefined : remove(m))}
                    title={t('members.remove')}
                  >
                    <Trash3 size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Add-member search box ───────────────────────────────────────────────────

function AddMemberBox({ existingIds, onPick, onClose }) {
  const { t } = useLocale();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced username search (mirrors the mobile add-member flow).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = setTimeout(() => {
      let alive = true;
      searchUsers(term)
        .then((rows) => {
          if (!alive) return;
          setResults(rows.filter((r) => !existingIds.has(r.id)));
        })
        .catch(() => alive && setResults([]))
        .finally(() => alive && setLoading(false));
      return () => {
        alive = false;
      };
    }, 300);
    return () => clearTimeout(id);
  }, [q, existingIds]);

  return (
    <div className="add-member">
      <div className="add-member__search">
        <Search size={15} className="add-member__search-icon" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('members.searchPlaceholder')}
        />
        <button className="add-member__close" onClick={onClose} title={t('shopify.cancel')}>
          <XLg size={15} />
        </button>
      </div>

      {q.trim().length >= 2 && (
        <div className="add-member__results">
          {loading ? (
            <div className="add-member__muted">{t('shell.loading')}</div>
          ) : !results || results.length === 0 ? (
            <div className="add-member__muted">{t('members.searchEmpty')}</div>
          ) : (
            results.map((u) => {
              const username = u.username || u.userName || null;
              const display = username ? `@${username}` : t('members.anonymous');
              const initial = (username || '?')[0].toUpperCase();
              return (
                <button
                  key={u.id}
                  className="add-member__result"
                  onClick={() => onPick(u)}
                >
                  {u.avatarUrl ? (
                    <img className="add-member__avatar" src={u.avatarUrl} alt="" />
                  ) : (
                    <span className="add-member__avatar add-member__avatar--ph">
                      {initial}
                    </span>
                  )}
                  <span className="add-member__result-name">{display}</span>
                  <PersonPlus size={16} className="add-member__result-add" />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Not connected yet (Statisztikák) ────────────────────────────────────────

// The fit-check button is not live on the brand's webshop yet (embed settings
// status !== 'configured'): no stats to show, so a centred nudge to finish the
// setup instead of empty charts. `onSetup` opens Beállítások at the code section.
function NotConnectedState({ onSetup }) {
  const { t } = useLocale();
  return (
    <div className="not-connected" data-testid="not-connected">
      <span className="not-connected__icon"><BarChartLine size={30} /></span>
      <p className="not-connected__text">{t('embed.notConnected.stats')}</p>
      {onSetup && (
        <button type="button" className="btn-o btn-o--dark" onClick={onSetup} data-testid="not-connected-cta">
          {t('embed.notConnected.cta')}
        </button>
      )}
    </div>
  );
}

// ── Small helpers ───────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
